import querystring from 'querystring'
import StaticResourceTypes from './StaticResourceTypes'
import fetchApi from '../services/fetchApi'

// Dashboards
export function listDashboards({ stats }) {
  const params = stats ? 'stats=true' : ''
  return fetchApi(`/dashboards?${params}`, 'GET')
}

export function listDashboardsRecents(recents) {
  const params = { stats: true, 'recents[]': recents }
  const queryParams = querystring.stringify(params)
  return fetchApi(`/dashboards?${queryParams}`, 'GET')
}

export function searchPractices({
  searchString,
  products,
  pmsTypes,
  activeOnly,
}) {
  const searchParams = {}
  if (searchString.length) searchParams.searchString = searchString
  if (products.length) searchParams['products[]'] = products
  if (pmsTypes.length) searchParams['pmsTypes[]'] = pmsTypes
  if (activeOnly) searchParams.active = true
  const params = querystring.stringify(searchParams)
  return fetchApi(`/dashboards?stats=true&${params}`, 'GET')
}

// Stats
export function downloadStats() {
  return fetchApi('/download', 'GET')
}

// Practice
export function showPractice(practiceId) {
  return fetchApi(`/practices/${practiceId}`, 'GET')
}

export function createPractice(body) {
  return fetchApi(
    '/practices',
    'POST',
    JSON.stringify({
      ...body,
      onboarding: true,
    }),
  )
}

export function updatePractice(practiceId, body) {
  return fetchApi(`/practices/${practiceId}`, 'PATCH', JSON.stringify(body))
}

// Locations
export function getLocations(practiceId) {
  return fetchApi(`/practices/${practiceId}/locations`, 'GET')
}

export function updateLocation(practiceId, locationId, body) {
  return fetchApi(
    `/practices/${practiceId}/locations/${locationId}`,
    'PATCH',
    JSON.stringify(body),
  )
}

export function createLocation(practiceId, body) {
  return fetchApi(
    `/practices/${practiceId}/locations`,
    'POST',
    JSON.stringify(body),
  )
}

// Businesses
export function createBusiness(practiceId, body) {
  return fetchApi(
    `/practices/${practiceId}/businesses`,
    'POST',
    JSON.stringify(body),
  )
}

export function updateBusiness(practiceId, businessId, body) {
  return fetchApi(
    `/practices/${practiceId}/businesses/${businessId}`,
    'PATCH',
    JSON.stringify(body),
  )
}

export function deleteBusiness(practiceId, businessId) {
  return fetchApi(`/practices/${practiceId}/businesses/${businessId}`, 'DELETE')
}

// Extensions

export function getExtensions(practiceId) {
  return fetchApi(`/practices/${practiceId}/extensions`, 'GET')
}

export function updateExtensions(practiceId, extensions) {
  return fetchApi(
    `/practices/${practiceId}/extensions`,
    'PATCH',
    JSON.stringify({ extension_map: extensions }),
  )
}

// Pbx

export function getPbx(practiceId) {
  return fetchApi(`/practices/${practiceId}/pbxes`, 'GET')
}

export function updatePbx(practiceId, pbxId, pbx) {
  return fetchApi(
    `/practices/${practiceId}/pbxes/${pbxId}`,
    'PATCH',
    JSON.stringify(pbx),
  )
}

export function createPbx(practiceId, pbx) {
  return fetchApi(`/practices/${practiceId}/pbxes`, 'POST', JSON.stringify(pbx))
}

// Doctors

export function getDoctors(practiceId) {
  return fetchApi(`/practices/${practiceId}/doctors`, 'GET')
}

export function updateDoctor(practiceId, doctorId, body) {
  return fetchApi(
    `/practices/${practiceId}/doctors/${doctorId}`,
    'PATCH',
    JSON.stringify(body),
  )
}

export function createDoctor(practiceId, body) {
  return fetchApi(
    `/practices/${practiceId}/doctors`,
    'POST',
    JSON.stringify(body),
  )
}

// Users
export function getUsers(practiceId) {
  return fetchApi(`/practices/${practiceId}/users`, 'GET')
}

export function createUser(resource, body) {
  const practiceId =
    typeof resource === 'object' ? resource[0].split('-')[0] : resource
  return fetchApi(
    `/practices/${practiceId}/users`,
    'POST',
    JSON.stringify(body),
  )
}

export function removeUser(practiceId, userId) {
  return fetchApi(`/practices/${practiceId}/users/${userId}`, 'DELETE')
}

export function updateUser(practiceId, userId, body) {
  return fetchApi(
    `/practices/${practiceId}/users/${userId}`,
    'PATCH',
    JSON.stringify(body),
  )
}

// User Roles
// @userId should be 0 if logged in user adds the role to themselves
const updateUserRoles = (
  resource,
  userId = 'user',
  role = 'practice_admin',
) => {
  const body = {
    resource_id: resource,
    resource_type: StaticResourceTypes[role],
    role_name: role,
    user_id: userId,
  }
  return fetchApi('/user_roles', 'POST', JSON.stringify(body))
}

export function getUserRoles(userId = 'user') {
  return fetchApi(`/user_roles/${userId}`, 'GET')
}
export function removeUserFromPractice(
  practiceId,
  userId = 'user',
  role = 'practice_admin',
) {
  return fetchApi(
    `/user_roles/${userId}`,
    'DELETE',
    JSON.stringify({
      resource_id: practiceId,
      resource_type: 'Practice',
      role_name: role,
      user_id: userId,
    }),
  )
}

export function reassignUserToNewPractice(practiceId, userId = 'user') {
  return getUserRoles(userId).then(res => {
    const practiceAdminRole = res.roles.find(
      role => role.name === 'practice_admin',
    )
    if (practiceAdminRole && practiceAdminRole.resource_id) {
      return removeUserFromPractice(practiceAdminRole.resource_id, userId).then(
        () => updateUserRoles(practiceId, userId),
      )
    }
    return updateUserRoles(practiceId, userId)
  })
}

export const createAdmin = async (resource, body, role = 'practice_admin') => {
  try {
    const userRes = await createUser(resource, body)
    await updateUserRoles(resource, userRes.user.id, role)
  } catch (e) {
    return e
  }
}

export const updateAdmin = async (practiceId, userId, body) => {
  try {
    await updateUser(practiceId, userId, body)
    const resource =
      body.type === 'location_admin' ? body.locations : practiceId
    await updateUserRoles(resource, userId, body.type)
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e)
  }
}

// Remote Dbs
export function getRemoteDatabases(practiceId) {
  return fetchApi(`/practices/${practiceId}/remote_databases`, 'GET')
}

export function createRemoteDatabase(practiceId, body) {
  return fetchApi(
    `/practices/${practiceId}/remote_databases`,
    'POST',
    JSON.stringify(body),
  )
}

export function updateRemoteDatabase(practiceId, databaseId, body) {
  return fetchApi(
    `/practices/${practiceId}/remote_databases/${databaseId}`,
    'PATCH',
    JSON.stringify(body),
  )
}

export function checkDbCredentials(databaseId) {
  return fetchApi(`/remote_databases/${databaseId}/ping`, 'GET').catch(() =>
    Promise.reject(
      new Error(
        'Connection failed. Please make sure the database credentials are correct.',
      ),
    ),
  )
}

export function scheduleSync(databaseId) {
  return fetchApi(`/remote_databases/${databaseId}/sync`, 'POST')
}

// Phone Numbers
export function buyPhoneNumber(
  countryCode,
  areaCode,
  locationId,
  addressSID,
  bundleSID,
) {
  return fetchApi(
    '/phone_numbers',
    'POST',
    JSON.stringify({
      address_sid: addressSID,
      area_code: areaCode,
      bundle_sid: bundleSID,
      country_code: countryCode,
      location_id: locationId,
    }),
  )
}

export function addOfficeTextableNumber(
  phoneNumber,
  twilioSid,
  locationIds,
  preferredNumber,
  businessId,
) {
  return fetchApi(
    '/phone_numbers/manual_add',
    'POST',
    JSON.stringify({
      phone_number: {
        business_id: businessId,
        is_preferred: preferredNumber,
        location_ids: locationIds,
        phone_number: phoneNumber,
        twilio_sid: twilioSid,
      },
    }),
  )
}

export function updatePhoneNumber(
  phoneNumberId,
  preferred,
  businessId,
  locationIds,
) {
  return fetchApi(
    `/phone_numbers/${phoneNumberId}`,
    'PATCH',
    JSON.stringify({
      phone_number: {
        business_id: businessId,
        is_preferred: preferred,
        location_ids: locationIds,
      },
    }),
  )
}

export function deletePhoneNumber(phoneNumberId, locationId) {
  return fetchApi(
    `/phone_numbers/${phoneNumberId}`,
    'DELETE',
    JSON.stringify({
      phone_number: {
        location_ids: [locationId],
      },
    }),
  )
}

// Scheduling Widget
export function generateWidgetToken(origin, practiceId) {
  return fetchApi(
    '/schedule_widgets',
    'POST',
    JSON.stringify({
      origin,
      practice_id: practiceId,
    }),
  )
}

// Appointment Types
export function getAppointmentTypes(practiceId) {
  return fetchApi(`/practices/${practiceId}/appointment_types`, 'GET')
}

// Carestream v2 Customer Ids
export function getCarestreamCustomers() {
  return fetchApi('/carestream_customers', 'GET')
}

export function getAdAccounts() {
  return fetchApi('/ad_accounts', 'GET')
}

export function getAdAccountIdsForPractice(practiceId) {
  return fetchApi(`/practices/${practiceId}/practice_ad_accounts`, 'GET')
}

export function updateAdAccountIdsForPractice(practiceId, adAccountIds) {
  console.log(JSON.stringify({ ad_account_ids: adAccountIds }))
  return fetchApi(
    `/practices/${practiceId}/practice_ad_accounts`,
    'PATCH',
    JSON.stringify({ ad_account_ids: adAccountIds }),
  )
}



// WEBPACK FOOTER //
// ./src/common/requests.js